/**
 * OS Notification Context
 * Global toast notifications for RunAI OS
 */
import { createContext, useCallback, useContext, useState } from 'react'
import type { NotificationType, OSNotification } from './types'

interface NotificationContextValue {
  notifications: OSNotification[]
  notify: (type: NotificationType, title: string, message?: string, duration?: number) => void
  dismiss: (id: string) => void
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

let idCounter = 0

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<OSNotification[]>([])

  const notify = useCallback((type: NotificationType, title: string, message?: string, duration = 5000) => {
    const id = `notif-${++idCounter}`
    const notif: OSNotification = { id, type, title, message, duration, timestamp: new Date() }
    setNotifications(prev => [...prev.slice(-4), notif]) // max 5

    if (duration > 0) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id))
      }, duration)
    }
  }, [])

  const dismiss = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  return (
    <NotificationContext.Provider value={{ notifications, notify, dismiss }}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const ctx = useContext(NotificationContext)
  if (!ctx) { throw new Error('useNotifications must be inside NotificationProvider') }
  return ctx
}
